import { useEffect } from 'react';

import { DEVICE_STATUS, type DeviceStatus } from './constants';

/**
 * The CSS custom property the app bar's decorative band reads. Declared here rather than in the
 * stylesheet because this hook is its only writer.
 */
export const STATUS_BAND_PROPERTY = '--status-band';

/**
 * Tint for the wash across the top of the Device Details page, keyed to the device's live state:
 * green while running, grey while off, and a warm red once the device is unreachable or its
 * subscription has lapsed.
 */
const BAND_BY_STATUS: Partial<Record<DeviceStatus, string>> = {
  [DEVICE_STATUS.RUNNING]: 'var(--status-band-running)',
  [DEVICE_STATUS.OFF]: 'var(--status-band-off)',
  [DEVICE_STATUS.DISCONNECTED]: 'var(--status-band-disconnected)',
  [DEVICE_STATUS.EXPIRED]: 'var(--status-band-disconnected)',
};

/**
 * Tints the app bar's band to match a device's status, for as long as the calling page is mounted.
 *
 * The band lives in `<TopNav>`, above this page in the tree, so the colour is passed up through a
 * custom property on the document root rather than through props — the alternative would be
 * threading device state into shared layout chrome that has no business knowing about devices.
 * The property is removed on unmount, so every other page keeps the default cream.
 */
export function useStatusBand(status: DeviceStatus | null | undefined) {
  useEffect(() => {
    const band = status ? BAND_BY_STATUS[status] : undefined;
    if (!band) return;

    const root = document.documentElement;
    root.style.setProperty(STATUS_BAND_PROPERTY, band);
    // Braces matter: `removeProperty` returns a string, and an effect cleanup must return void.
    return () => {
      root.style.removeProperty(STATUS_BAND_PROPERTY);
    };
  }, [status]);
}
